import { type FC, useCallback, useMemo } from "react";
import styled from "styled-components";

export interface TimeRangeButtonsProps {
  startDate: string;
  endDate: string;
  onSelect: (rangeKey: string, start: string, end: string) => void;
}

const RANGES = [
  { key: "24h", label: "24h", days: 1 },
  { key: "7d", label: "7d", days: 7 },
  { key: "30d", label: "30d", days: 30 },
  { key: "90d", label: "90d", days: 90 },
] as const;

function computeRange(days: number): { start: string; end: string } {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - days);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const Pill = styled.button<{ $active: boolean }>`
  font-family: var(--vmx-font);
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  padding: 6px 12px;
  border-radius: 9999px;
  border: 1.5px solid
    ${(p) => (p.$active ? "var(--vmx-accent)" : "var(--vmx-gray-300)")};
  background: ${(p) => (p.$active ? "var(--vmx-accent)" : "var(--vmx-white)")};
  color: ${(p) => (p.$active ? "var(--vmx-white)" : "var(--vmx-gray-600)")};
  cursor: pointer;
  transition: all var(--vmx-transition);

  &:hover {
    border-color: var(--vmx-accent);
    color: ${(p) => (p.$active ? "var(--vmx-white)" : "var(--vmx-accent)")};
  }
`;

export const TimeRangeButtons: FC<TimeRangeButtonsProps> = ({
  startDate,
  endDate,
  onSelect,
}) => {
  const matchedKey = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    if (endDate !== todayStr) return null;
    for (const { key, days } of RANGES) {
      const { start } = computeRange(days);
      if (startDate === start) return key;
    }
    return null;
  }, [startDate, endDate]);

  const handleClick = useCallback(
    (key: string, days: number) => {
      const { start, end } = computeRange(days);
      onSelect(key, start, end);
    },
    [onSelect],
  );

  return (
    <Wrapper data-testid="time-range-buttons">
      {RANGES.map(({ key, label, days }) => (
        <Pill
          key={key}
          type="button"
          $active={matchedKey === key}
          className={matchedKey === key ? "vmx-btn-active" : ""}
          onClick={() => handleClick(key, days)}
        >
          {label}
        </Pill>
      ))}
    </Wrapper>
  );
};
